import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Car, 
  MessageSquare, 
  Image, 
  Settings,
  Mail,
  KeyRound,
  Users,
  BarChart3,
  FileText,
  Newspaper,
  ExternalLink,
  CalendarCheck,
  HelpCircle,
  Lock
} from 'lucide-react';
import { useAdminLanguage } from '@/context/AdminLanguageContext';
import { useAuth } from '@/context/AuthContext';

// App definitions with translation keys and permission mapping
const getAdminApps = (t) => [
  { 
    id: 'analytics',
    path: '/admin/analytics', 
    icon: BarChart3, 
    label: t('dashboard_stats_analytics') || 'Analytics',
    color: 'bg-purple-500',
    description: t('dashboard_stats_desc') || 'Statistics',
    permissionKey: 'analytics'
  },
  { 
    id: 'cars',
    path: '/admin/cars', 
    icon: Car, 
    label: t('sidebar_cars'),
    color: 'bg-blue-500',
    description: t('cars_title'),
    permissionKey: 'cars'
  },
  { 
    id: 'test-drive',
    path: '/admin/test-drive', 
    icon: CalendarCheck, 
    label: t('sidebar_test_drives'),
    color: 'bg-teal-500',
    description: t('test_drives_calendar'),
    permissionKey: 'test_drive'
  },
  { 
    id: 'customers',
    path: '/admin/customers', 
    icon: Users, 
    label: t('sidebar_customers'),
    color: 'bg-amber-500',
    description: 'CRM',
    permissionKey: 'customers'
  },
  { 
    id: 'contracts',
    path: '/admin/contracts', 
    icon: FileText, 
    label: t('sidebar_contracts'),
    color: 'bg-emerald-500',
    description: t('contracts_title'),
    permissionKey: 'contracts'
  },
  { 
    id: 'inquiries',
    path: '/admin/inquiries', 
    icon: MessageSquare, 
    label: t('sidebar_inquiries'),
    color: 'bg-rose-500',
    description: t('inquiries_title'),
    permissionKey: 'inquiries'
  },
  { 
    id: 'blog',
    path: '/admin/blog', 
    icon: Newspaper, 
    label: t('sidebar_blog'),
    color: 'bg-indigo-500',
    description: 'News',
    permissionKey: 'blog'
  },
  { 
    id: 'newsletter',
    path: '/admin/newsletter', 
    icon: Mail, 
    label: t('sidebar_newsletter'),
    color: 'bg-cyan-500',
    description: t('newsletter_subscribers'),
    permissionKey: 'newsletter'
  },
  { 
    id: 'settings',
    path: '/admin/settings', 
    icon: Settings, 
    label: t('sidebar_settings'),
    color: 'bg-slate-500',
    description: t('settings_title'),
    permissionKey: 'settings'
  },
  { 
    id: 'password',
    path: '/admin/password', 
    icon: KeyRound, 
    label: t('settings_account_password'),
    color: 'bg-orange-500',
    description: t('status_active') || 'Security',
    permissionKey: null // Always allowed
  },
  { 
    id: 'support',
    path: '/admin/support', 
    icon: HelpCircle, 
    label: t('dashboard_support'),
    color: 'bg-red-500',
    description: t('dashboard_support'),
    permissionKey: null // Always allowed
  },
  { 
    id: 'website',
    path: '/', 
    icon: ExternalLink, 
    label: 'Website',
    color: 'bg-gold',
    description: t('action_back') || 'View Website',
    external: true,
    permissionKey: null // Always allowed
  },
];

export default function AdminDashboard() {
  const { t, adminLang } = useAdminLanguage();
  const { user } = useAuth();
  const ADMIN_APPS = getAdminApps(t);
  
  // Check if user has permission for a module
  const hasPermission = (permissionKey) => {
    // Main admin has all permissions
    if (user?.is_main_admin || user?.username === 'admin') return true;
    // No permission key means always allowed
    if (!permissionKey) return true;
    // Check user permissions
    return user?.permissions?.[permissionKey] === true;
  };
  
  return (
    <div data-testid="admin-dashboard" className="min-h-[80vh]">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="font-heading text-3xl md:text-4xl font-light mb-2">
          {t('dashboard_admin_area')}
        </h1>
        <p className="text-[#666666]">
          {t('dashboard_choose_option')}
        </p>
      </div>

      {/* App Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6 max-w-5xl mx-auto">
        {ADMIN_APPS.map((app) => {
          const Icon = app.icon;
          const allowed = hasPermission(app.permissionKey);
          
          // If not allowed, show disabled version
          if (!allowed) {
            return (
              <div
                key={app.id}
                className="flex flex-col items-center p-4 md:p-6 bg-gray-100 rounded-2xl border border-gray-200 cursor-not-allowed opacity-50"
                data-testid={`app-${app.id}-disabled`}
                title={t('sidebar_logged_in_as') === 'Logged in as' ? 'No access' : 'Kein Zugriff'}
              >
                {/* App Icon */}
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-400 rounded-2xl flex items-center justify-center mb-3 relative">
                  <Icon size={28} className="text-white" strokeWidth={1.5} />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                    <Lock size={12} className="text-white" />
                  </div>
                </div>
                
                {/* App Label */}
                <span className="font-medium text-sm md:text-base text-center text-gray-500">
                  {app.label}
                </span>
                
                {/* App Description (hidden on small screens) */}
                <span className="hidden md:block text-xs text-gray-400 mt-1">
                  {app.description}
                </span>
              </div>
            );
          }
          
          const Component = app.external ? 'a' : Link;
          const linkProps = app.external 
            ? { href: app.path, target: '_blank', rel: 'noopener noreferrer' }
            : { to: app.path };
          
          return (
            <Component
              key={app.id}
              {...linkProps}
              className="group flex flex-col items-center p-4 md:p-6 bg-white rounded-2xl border border-[#E5E5E5] hover:border-gold hover:shadow-lg transition-all duration-300"
              data-testid={`app-${app.id}`}
            >
              {/* App Icon */}
              <div className={`w-14 h-14 md:w-16 md:h-16 ${app.color} rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                <Icon size={28} className="text-white" strokeWidth={1.5} />
              </div>
              
              {/* App Label */}
              <span className="font-medium text-sm md:text-base text-center text-[#0A0A0A] group-hover:text-gold transition-colors">
                {app.label}
              </span>
              
              {/* App Description (hidden on small screens) */}
              <span className="hidden md:block text-xs text-[#999999] mt-1">
                {app.description}
              </span>
            </Component>
          );
        })}
      </div>

      {/* Footer Tip */}
      <div className="mt-12 text-center">
        <p className="text-sm text-[#999999]">
          {t('dashboard_tip')}
        </p>
      </div>
    </div>
  );
}
